import * as React from 'react'
import { Button } from 'src/design-system'
import { X, Plus, MagnifyingGlass } from '@phosphor-icons/react'
import { SuggestedSkill } from './types'
import styles from '../../styles/scrollbar-hide.module.css'

type AddSearchViewProps = {
  onClose: () => void
  onSelectSkill: (skill: SuggestedSkill) => void
  onCreateNewSkill?: () => void
}

export const AddSearchView: React.FC<AddSearchViewProps> = ({
  onClose,
  onSelectSkill,
  onCreateNewSkill,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [suggestions, setSuggestions] = React.useState<SuggestedSkill[]>([])
  const [loading, setLoading] = React.useState(false)

  const fetchSuggestions = React.useCallback(async (term: string) => {
    if (!term.trim()) {
      setSuggestions([])
      return
    }

    setLoading(true)
    try {
      const res = await fetch(
        `/api/v1/skills/suggest?term=${encodeURIComponent(term)}`
      )
      if (res.ok) {
        const json = await res.json()
        setSuggestions(json.data || [])
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuggestions(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm, fetchSuggestions])

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Add skill</h2>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-100 text-gray-500"
        >
          <X className="h-5 w-5" weight="bold" />
        </button>
      </div>

      <div className={`flex-1 overflow-y-auto p-4 ${styles.hideScrollbar}`}>
        <button
          onClick={onCreateNewSkill}
          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 mb-4"
        >
          <div className="w-10 h-10 rounded-lg bg-theme-90 flex items-center justify-center">
            <Plus className="h-5 w-5 text-theme-40" weight="bold" />
          </div>
          <span className="text-sm font-medium text-gray-900">
            Create new personal skill
          </span>
        </button>

        <div className="relative mb-4">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
          />
        </div>

        {searchTerm && (
          <>
            <p className="text-xs text-gray-500 mb-3">
              Sorted by: most related to your role
            </p>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
              </div>
            ) : suggestions.length > 0 ? (
              <div className="space-y-1 px-4">
                {suggestions.map((skill) => (
                  <div
                    key={skill.id}
                    className="flex items-center justify-between py-3 border-b border-gray-100"
                  >
                    <span className="text-sm text-gray-900">{skill.name}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSelectSkill(skill)}
                      className="text-sm px-4 text-gray-900 hover:bg-gray-200 border-1.5 border-black"
                    >
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                No skills found for &quot;{searchTerm}&quot;
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
